// @ts-check

/**
 * @file Builds board layout theme color palettes.
 */

import fetchingObject from "fetching-object"

/** @typedef {"dark" | "light"} BoardThemeName */

/**
 * @typedef {object} BoardThemeColors
 * @property {string} screen App background.
 * @property {string} sidebar Sidebar background.
 * @property {string} sidebarBorder Sidebar right border.
 * @property {string} sidebarText Sidebar primary text.
 * @property {string} sidebarMutedText Sidebar muted/inactive text.
 * @property {string} sidebarActiveText Text color for selected sidebar items.
 * @property {string} sidebarActiveBackground Sidebar active item background.
 * @property {string} topBar Top-bar background.
 * @property {string} topBarBorder Top-bar bottom border.
 * @property {string} topBarText Top-bar title text.
 * @property {string} bottomNav Bottom-nav background.
 * @property {string} bottomNavBorder Bottom-nav top border.
 * @property {string} primary Primary action color.
 * @property {string} onPrimary Text/icon color on primary background.
 * @property {string} secondary Secondary action color (used for "alternate" filled buttons such as Export).
 * @property {string} onSecondary Text/icon color on secondary background.
 * @property {string} surface Card / panel background that sits on top of the screen background.
 * @property {string} surfaceMuted Subtler panel background (zebra rows, secondary cards, hovered rows).
 * @property {string} surfaceRaised Elevated surface (modals, popovers, dropdowns).
 * @property {string} surfaceSunken Inset background (search inputs, toolbars, code blocks).
 * @property {string} text Primary body text.
 * @property {string} textMuted Secondary body text (subtitles, captions).
 * @property {string} textInverse Text on filled / dark backgrounds where the surface is inverted relative to the body.
 * @property {string} border Standard control / card border.
 * @property {string} borderStrong Higher-contrast border for focused or selected controls.
 * @property {string} divider Hairline separators between rows / sections.
 * @property {string} link Link / navigable accent text.
 * @property {string} chipBackground Default neutral chip background.
 * @property {string} chipText Default neutral chip text.
 * @property {string} chipActiveBackground Selected / active chip background.
 * @property {string} chipActiveText Selected / active chip text.
 * @property {string} success Success accent (icons, badges, text).
 * @property {string} successBackground Success-tinted background (banners, badges).
 * @property {string} successText Text rendered over a success background.
 * @property {string} danger Danger / destructive accent.
 * @property {string} dangerBackground Danger-tinted background.
 * @property {string} dangerText Text rendered over a danger background.
 * @property {string} warning Warning accent.
 * @property {string} warningBackground Warning-tinted background.
 * @property {string} warningText Text rendered over a warning background.
 * @property {string} info Informational accent.
 * @property {string} infoBackground Informational-tinted background.
 * @property {string} infoText Text rendered over an info background.
 * @property {string} placeholder Input placeholder text.
 * @property {string} disabledBackground Background for disabled controls.
 * @property {string} disabledText Text on disabled controls.
 * @property {string} overlay Scrim color used behind modals / drawers.
 * @property {string} focusRing Focus / keyboard outline color.
 */

/** @type {BoardThemeColors} */
const lightColors = {
  border: "#e2e8f0",
  borderStrong: "#cbd5e1",
  bottomNav: "#ffffff",
  bottomNavBorder: "#e2e8f0",
  chipActiveBackground: "#dbeafe",
  chipActiveText: "#1d4ed8",
  chipBackground: "#f1f5f9",
  chipText: "#475569",
  danger: "#dc2626",
  dangerBackground: "#fee2e2",
  dangerText: "#b91c1c",
  disabledBackground: "#f1f5f9",
  disabledText: "#94a3b8",
  divider: "#e2e8f0",
  focusRing: "#1d4ed8",
  info: "#0284c7",
  infoBackground: "#e0f2fe",
  infoText: "#075985",
  link: "#1d4ed8",
  onPrimary: "#ffffff",
  onSecondary: "#ffffff",
  overlay: "rgba(15, 23, 42, 0.4)",
  placeholder: "#94a3b8",
  primary: "#1d4ed8",
  secondary: "#0f766e",
  screen: "#f8fafc",
  sidebar: "#ffffff",
  sidebarActiveBackground: "rgba(29, 78, 216, 0.12)",
  sidebarActiveText: "#1d4ed8",
  sidebarBorder: "#e2e8f0",
  sidebarMutedText: "#64748b",
  sidebarText: "#0f172a",
  success: "#16a34a",
  successBackground: "#dcfce7",
  successText: "#166534",
  surface: "#ffffff",
  surfaceMuted: "#f8fafc",
  surfaceRaised: "#ffffff",
  surfaceSunken: "#f1f5f9",
  text: "#0f172a",
  textInverse: "#ffffff",
  textMuted: "#64748b",
  topBar: "#ffffff",
  topBarBorder: "#e2e8f0",
  topBarText: "#0f172a",
  warning: "#d97706",
  warningBackground: "#fef3c7",
  warningText: "#92400e"
}
/** @type {Partial<Record<BoardThemeName, BoardThemeColors>>} */
const boardThemeColorsCache = {}

/** @type {BoardThemeColors} */
const darkColors = {
  border: "#1e293b",
  borderStrong: "#334155",
  bottomNav: "#0f172a",
  bottomNavBorder: "#1e293b",
  chipActiveBackground: "rgba(96, 165, 250, 0.18)",
  chipActiveText: "#bfdbfe",
  chipBackground: "#1e293b",
  chipText: "#cbd5e1",
  danger: "#f87171",
  dangerBackground: "rgba(248, 113, 113, 0.16)",
  dangerText: "#fecaca",
  disabledBackground: "#1e293b",
  disabledText: "#64748b",
  divider: "#1e293b",
  focusRing: "#60a5fa",
  info: "#38bdf8",
  infoBackground: "rgba(56, 189, 248, 0.16)",
  infoText: "#bae6fd",
  link: "#60a5fa",
  onPrimary: "#ffffff",
  onSecondary: "#022c22",
  overlay: "rgba(2, 6, 23, 0.7)",
  placeholder: "#64748b",
  primary: "#60a5fa",
  secondary: "#2dd4bf",
  screen: "#0f172a",
  sidebar: "#0f172a",
  sidebarActiveBackground: "rgba(29, 78, 216, 0.2)",
  sidebarActiveText: "#93c5fd",
  sidebarBorder: "#1e293b",
  sidebarMutedText: "#94a3b8",
  sidebarText: "#e2e8f0",
  success: "#4ade80",
  successBackground: "rgba(74, 222, 128, 0.16)",
  successText: "#bbf7d0",
  surface: "#1e293b",
  surfaceMuted: "#172033",
  surfaceRaised: "#1e293b",
  surfaceSunken: "#0b1220",
  text: "#e2e8f0",
  textInverse: "#0f172a",
  textMuted: "#94a3b8",
  topBar: "#0f172a",
  topBarBorder: "#1e293b",
  topBarText: "#f8fafc",
  warning: "#fbbf24",
  warningBackground: "rgba(251, 191, 36, 0.16)",
  warningText: "#fde68a"
}

/**
 * Selects the board color palette for a theme name. The returned
 * object is wrapped in `fetching-object` so reading a token that
 * doesn't exist on the palette throws `PropertyNotFoundError` —
 * this catches typos at the call site instead of silently rendering
 * `undefined` into a style and quietly drifting between themes.
 * @param {BoardThemeName} themeName Theme identifier to resolve.
 * @returns {BoardThemeColors} Color palette for the requested theme.
 */
export function buildBoardThemeColors(themeName) {
  boardThemeColorsCache[themeName] ||= fetchingObject(themeName === "dark" ? darkColors : lightColors)

  return boardThemeColorsCache[themeName]
}

/**
 * Detects the operating-system theme preference.
 * @returns {BoardThemeName} Preferred theme name.
 */
export function detectSystemTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light"

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}
